import { CTA_CTE, ENVIO_A_CARGO_COMPRADOR } from '../../constants/mediosEnviosPagos.js'
import { validarRangoFechas } from '../dateValidation.js'
import { calcPrecioPublicacion } from './matchHelpers.js'

export const MAPA_PUBLICACIONES = {
  categoriaSeleccionada: {
    filterFn: (p, v) => {
      if (!v) {
        return true
      }
      if (!p.categoriaRoot) {
        return false
      }
      try {
        return JSON.parse(p.categoriaRoot)
          .map((c) => c.id)
          .includes(Number(v))
      } catch {
        return false
      }
    }
  },

  tipoVendedor: {
    titulo: 'Vendedor',
    opciones: ({ perfiles = [] } = {}) =>
      perfiles.map((p) => ({
        label: p.nombre,
        valor: p.idPerfiles
      })),
    filterFn: (p, v) =>
      Array.isArray(v) ? v.includes(Number(p.idPerfil)) : Number(p.idPerfil) === v
  },

  estadoProducto: {
    titulo: 'Estado',
    opciones: () => [
      { label: 'Nuevo', valor: 'nuevo' },
      { label: 'Usado', valor: 'usado' }
    ],
    filterFn: (p, v) => (v === 'nuevo' ? p.nuevo === true : p.nuevo === false)
  },

  estadoPublicacion: {
    titulo: 'Estado de publicación',
    opciones: ({ publicaciones = [] } = {}) => [
      ...new Map(
        publicaciones
          .filter((p) => p.idPublicacionEstado !== null && p.idPublicacionEstado !== undefined)
          .map((p) => [
            p.idPublicacionEstado,
            {
              label: String(p.publicacionEstado ?? p.idPublicacionEstado),
              valor: p.idPublicacionEstado
            }
          ])
      ).values()
    ],
    filterFn: (p, v) => p.idPublicacionEstado === v
  },

  precioDesde: { tipo: 'precio', filterFn: (p, v) => calcPrecioPublicacion(p) >= Number(v) },
  precioHasta: { tipo: 'precio', filterFn: (p, v) => calcPrecioPublicacion(p) <= Number(v) },

  exposicion: {
    titulo: 'Tipo de Exposición',
    opciones: ({ exposiciones = [] } = {}) =>
      exposiciones.map((e) => ({ label: e.nombre, valor: e.idPublicacionExposicion })),
    filterFn: (p, v) => p.idPublicacionExposicion === v
  },

  usuarioVendedor: {
    titulo: 'Vendedor',
    opciones: ({ publicaciones = [] } = {}) =>
      [
        ...new Map(
          publicaciones
            .filter((p) => p.idVendedor && p.vendedorNombre)
            .map((p) => [p.idVendedor, { label: p.vendedorNombre.trim(), valor: p.idVendedor }])
        ).values()
      ].sort((a, b) => a.label.localeCompare(b.label)),
    filterFn: (p, v) => Number(p.idVendedor) === Number(v)
  },

  listaVenta: {
    titulo: 'Listas Ventas',
    opciones: ({ listasVentaConDatos = [] } = {}) =>
      listasVentaConDatos.map((l) => ({ label: l.nombre, valor: l.idListaVenta })),
    filterFn: (p, v) => p.idListaVenta === v
  },

  listaEnvioVencida: {
    titulo: 'Lista Envío Vencida',
    opciones: ({ listasEnviosVencidas = [] } = {}) =>
      listasEnviosVencidas.map((l) => ({ label: l.label, valor: l.value })),
    filterFn: (p, v) => {
      const medio = (p.mediosEnvios ?? []).find((m) => m.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR)
      return medio?.nombreLista?.toString().trim().toLowerCase() === v
    }
  },

  textoBusqueda: {
    placeholder: 'Buscar por título o NRO publicación...',
    filterFn: (p, v) => {
      const val = v.trim().toLowerCase()
      if (!val) return true
      return p.titulo.toLowerCase().includes(val) || p.idPublicacion.toString().includes(val)
    }
  },

  provinciaSeleccionada: {
    titulo: 'Provincia',
    opciones: ({ provincias = [] }) =>
      provincias.map((prov) => ({
        label: prov.charAt(0).toUpperCase() + prov.slice(1),
        valor: prov
      })),
    filterFn: (p, v, filtros) => {
      if (p.provinciaVendedor !== v) return false
      return !filtros.localidadSeleccionada || p.localidadVendedor === filtros.localidadSeleccionada
    }
  },

  localidadSeleccionada: {
    titulo: 'Localidad',
    opciones: ({ localidadesFiltradasPorProvincia = [] }) =>
      localidadesFiltradasPorProvincia.map((loc) => ({
        label: loc.charAt(0).toUpperCase() + loc.slice(1),
        valor: loc
      })),
    filterFn: (p, v, filtros) => {
      if (filtros.provinciaSeleccionada) {
        return true
      }
      return p.localidadVendedor === v
    }
  },

  proveedorPropio: {
    titulo: 'Proveedores propios en e-Buyplace',
    opciones: () => [
      { label: 'Sí', valor: 'si' },
      { label: 'No', valor: 'no' }
    ],
    filterFn: (p, v, filtros) => {
      if (!v) {
        return true
      }
      const ids = (filtros.proveedoresPropios ?? []).map((proveedor) => proveedor.idUsuario)
      if (v === 'si') {
        return ids.includes(p.idVendedor)
      }

      return !ids.includes(p.idVendedor)
    }
  },

  vendedoresCtaCte: {
    titulo: 'Vendedores con Cuenta Corriente',
    opciones: () => [
      { label: 'Sí', valor: 'si' },
      { label: 'No', valor: 'no' }
    ],
    filterFn: (p, v, filtros) => {
      const empresasHabilitadas = (filtros.vendedoresCtaCteData ?? [])
        .filter((vendedor) => vendedor.empidVendedor && vendedor.habilita)
        .map((vendedor) => String(vendedor.empidVendedor))

      const habilitados =
        v === 'si'
          ? empresasHabilitadas.includes(String(p.empid))
          : !empresasHabilitadas.includes(String(p.empid))

      return habilitados && p.mediosPagos?.some((m) => m.idMedioPago === CTA_CTE)
    }
  },

  fechaDesde: {
    filterFn: (p, v) => validarRangoFechas(v, p?.fechaPublicacion ?? p.fechaPublicacion)
  },
  fechaHasta: {
    filterFn: (p, v) => validarRangoFechas(p?.fechaPublicacion ?? p.fechaPublicacion, v)
  },

  filtrosSeleccionados: {
    filterFn: (p, v, filtros) => {
      const { caracteristicasActuales = [], mapEtiquetaIds = {} } = filtros
      const filtrosActivos = Object.entries(v).filter(([, valor]) => valor)
      if (!filtrosActivos.length) {
        return true
      }
      return filtrosActivos.every(([etiqueta, valor]) => {
        const valorNorm = valor.toString().trim().toLowerCase()
        const etiquetaNorm = etiqueta.toString().trim().toLowerCase()
        const idsAsociados = mapEtiquetaIds[etiqueta] || []
        return caracteristicasActuales.some((c) => {
          const filterFnId = idsAsociados.includes(c.idMetaDato)
          const filterFnEtiqueta = c.etiqueta?.toString().trim().toLowerCase() === etiquetaNorm
          const filterFnValor = (c.valor ?? '').toString().trim().toLowerCase() === valorNorm
          return (
            c.idPublicacion === p.idPublicacion && filterFnValor && (filterFnId || filterFnEtiqueta)
          )
        })
      })
    }
  },

  caracteristicasActuales: { filterFn: () => true },
  mapEtiquetaIds: { filterFn: () => true }
}
